import { getOS, OS } from 'utils/os-detection';

export interface ShortcutDefinition {
  shortcut: string;
  description?: string;
  alwaysAvailable?: boolean;
  /** Is this shortcut only exposed to the experimental user group? */
  experimental?: boolean;
  /** Does this event bubble up (NOT preventingDefault) when it's on input fields */
  fireFromInputs?: boolean;
}

export const SHOW_HELP = 'SHOW_HELP';

// Calendar actions (though some register globally)
export const GOTO_CALENDAR_TODAY = 'GOTO_CALENDAR_TODAY';
export const CREATE_NEW_HOUR_DRAFT = 'CREATE_NEW_HOUR_DRAFT';
export const CALENDAR_NEXT_PERIOD = 'CALENDAR_NEXT_PERIOD';
export const CALENDAR_PREVIOUS_PERIOD = 'CALENDAR_PREVIOUS_PERIOD';
export const CALENDAR_DAY_VIEW = 'CALENDAR_DAY_VIEW';
export const CALENDAR_WEEK_VIEW = 'CALENDAR_WEEK_VIEW';
export const CALENDAR_MONTH_VIEW = 'CALENDAR_MONTH_VIEW';
export const CALENDAR_CLEAR_SELECTION = 'CALENDAR_CLEAR_SELECTION';

// Projects
export const CREATE_NEW_PROJECT_DRAFT = 'CREATE_NEW_PROJECT_DRAFT';
export const FOCUS_PROJECT_SEARCH = 'FOCUS_PROJECT_SEARCH';

// Projects
export const CREATE_NEW_TASK = 'CREATE_NEW_TASK';

// Users
export const CREATE_NEW_USER_DRAFT = 'CREATE_NEW_USER_DRAFT';
export const FOCUS_USER_SEARCH = 'FOCUS_USER_SEARCH';

// Reports
export const CREATE_NEW_REPORT_DRAFT = 'CREATE_NEW_REPORT_DRAFT';

// Client modal
export const CREATE_NEW_CLIENT = 'CREATE_NEW_CLIENT';

// (New) Timeline
export const PAN_TIMELINE = 'PAN_TIMELINE';
export const ZOOM_TIMELINE = 'ZOOM_TIMELINE';
export const SELECT_TIMELINE_RANGE = 'SET_TIMELINE_TIMESTAMP_RANGE';
export const REMOVE_TIMELINE_RANGE = 'REMOVE_TIMELINE_TIMESTAMP_RANGE';

// Training
export const SELECT_PROJECT_1 = 'SELECT_PROJECT_1';
export const SELECT_PROJECT_2 = 'SELECT_PROJECT_2';
export const SELECT_PROJECT_3 = 'SELECT_PROJECT_3';
export const SELECT_PROJECT_4 = 'SELECT_PROJECT_4';
export const SELECT_PROJECT_5 = 'SELECT_PROJECT_5';
export const SEARCH_PROJECTS = 'SEARCH_PROJECTS';

export const CLOSE_STANDALONE_HOUR_EDITOR = 'CLOSE_STANDALONE_HOUR_EDITOR';

export const modifierKey = getOS() !== OS.Mac ? 'alt' : 'ctrl';
export const metaKey = getOS() !== OS.Mac ? 'ctrl' : 'cmd';

export const keymap = {
  global: {
    description: 'Global (use anywhere)',
    shortcuts: {
      [SHOW_HELP]: {
        shortcut: '?',
        description: 'Toggle this help menu',
        alwaysAvailable: true,
      },
      [CREATE_NEW_HOUR_DRAFT]: {
        shortcut: `${modifierKey}+e`,
        description: 'Create new Entry',
      },
      [CREATE_NEW_PROJECT_DRAFT]: {
        shortcut: `${modifierKey}+p`,
        description: 'Create new Project',
      },
      [CREATE_NEW_TASK]: {
        shortcut: `${modifierKey}+t`,
        description: 'Create new Task',
      },
      [GOTO_CALENDAR_TODAY]: {
        shortcut: `${modifierKey}+d`,
        description: `Show ‘Today’ in Hours`,
      },
    },
  },
  hourEditor: {
    description: 'Hour Editor',
    shortcuts: {
      [CLOSE_STANDALONE_HOUR_EDITOR]: {
        shortcut: 'escape',
        description: 'Close standalone Hour Editor',
      },
    },
  },
  calendar: {
    description: 'Hours',
    shortcuts: {
      [CREATE_NEW_HOUR_DRAFT]: {
        shortcut: `${modifierKey}+n`,
        description: 'Create new Entry',
      },
      [CALENDAR_NEXT_PERIOD]: {
        shortcut: `n`,
        description: 'Go to next day/week/month',
      },
      [CALENDAR_PREVIOUS_PERIOD]: {
        shortcut: `p`,
        description: 'Go to previous day/week/month',
      },
      [CALENDAR_DAY_VIEW]: {
        shortcut: `d`,
        description: 'Day view',
      },
      [CALENDAR_WEEK_VIEW]: {
        shortcut: `w`,
        description: 'Week view',
      },
      [CALENDAR_MONTH_VIEW]: {
        shortcut: `m`,
        description: 'Month view',
      },
      [CALENDAR_CLEAR_SELECTION]: {
        shortcut: `Escape`,
        description: 'Clear selection',
      },
    },
  },
  projects: {
    description: 'Projects',
    shortcuts: {
      [CREATE_NEW_PROJECT_DRAFT]: {
        shortcut: `${modifierKey}+n`,
        description: 'Create new Project',
      },
      [FOCUS_PROJECT_SEARCH]: {
        shortcut: `${modifierKey}+f`,
        description: 'Search Projects',
      },
    },
  },
  users: {
    description: 'People',
    shortcuts: {
      [CREATE_NEW_USER_DRAFT]: { shortcut: `${modifierKey}+n`, description: 'Invite a new person' },
      [FOCUS_USER_SEARCH]: { shortcut: `${modifierKey}+f`, description: 'Search people' },
    },
  },
  reports: {
    description: 'Report templates',
    shortcuts: {
      [CREATE_NEW_REPORT_DRAFT]: {
        shortcut: `${modifierKey}+n`,
        description: 'Create new template',
      },
    },
  },
  clients: {
    description: 'Clients Modal',
    shortcuts: {
      [CREATE_NEW_CLIENT]: {
        shortcut: `${modifierKey}+n`,
        description: 'Create new client',
      },
    },
  },
  timeline: {
    description: '(New) Timeline',
    shortcuts: {
      [PAN_TIMELINE]: {
        shortcut: `space+click`,
        description: 'Pan around timeline',
      },
      [ZOOM_TIMELINE]: {
        shortcut: `${metaKey}+scrollwheel`,
        description: 'Zoom timeline',
      },
      [SELECT_TIMELINE_RANGE]: {
        shortcut: `click+drag`,
        description: 'Set a timestamp',
      },
      [REMOVE_TIMELINE_RANGE]: {
        shortcut: `alt+click+drag`,
        description: 'Remove a timestamp',
      },
    },
  },
} satisfies {
  [key: string]: {
    description: string;
    shortcuts: {
      [key: string]: ShortcutDefinition;
    };
  };
};

export default keymap;
